import { useEffect } from 'react';

import { PrismicCacheStore } from '@/models/Prismic.ts';
import { PrismicDataType, PrismicReturnType } from '@/models/PrismicHookTypes.ts';

import { usePrismicCache, usePrismicFetchingStore, usePrismicRefStore } from '@/stores/prismicStore.ts';

type PrismicHook<T extends PrismicDataType> = {
    data: PrismicReturnType[T] | undefined;
};

const usePrismic = <T extends PrismicDataType>(type: T): PrismicHook<T> => {
    const { master_ref, is_ref_set } = usePrismicRefStore();
    const cached_data: PrismicCacheStore[T] = usePrismicCache((state) => state[type]);
    const is_cache_set = usePrismicCache((state) => state.is_cache_set);

    const is_cache_uptodate: boolean = is_ref_set && cached_data && cached_data.ref === master_ref;

    useEffect(() => {
        if (!is_ref_set || !master_ref || is_cache_uptodate || !is_cache_set) return;

        usePrismicFetchingStore.setState((state) => ({
            fetching_queue: [...state.fetching_queue, type],
        }));
    }, [master_ref, type, is_cache_uptodate, is_ref_set, is_cache_set]);

    return {
        data: is_cache_uptodate ? (cached_data?.data as PrismicReturnType[T] | undefined) : undefined,
    };
};

export default usePrismic;
